import { findFirst } from "./data-lookup.js";
/**
 * Create a spline out of given data samples
 * @param time returns the timestamp for the data point at given index
 * @param data returns the signal value for the data point at given index
 * @param codec the codec to use for the given data
 * @param gapDetectionThreshold data points that are spaced apart less then this parameter are considered a continuous signal
 * @param targetInterval the target interval of the individual path/spline segments
 * @returns A list of spline (bezier) segment lists
 */
export const createSpline = (time, data, codec, gapDetectionThreshold, targetInterval) => {
    let spline = [];
    let section = [];
    const s1i = undefined;
    const s0i = findFirst(0, data);
    const e0i = s0i !== undefined ? findFirst(s0i + 1, data) : undefined;
    const e1i = e0i !== undefined ? findFirst(e0i + 1, data) : undefined;
    let s1 = s1i !== undefined
        ? { t: time(s1i), v: data(s1i), i: s1i }
        : undefined;
    let s0 = s0i !== undefined
        ? { t: time(s0i), v: data(s0i), i: s0i }
        : undefined;
    let e0 = e0i !== undefined
        ? { t: time(e0i), v: data(e0i), i: e0i }
        : undefined;
    let e1 = e1i !== undefined
        ? { t: time(e1i), v: data(e1i), i: e1i }
        : undefined;
    if (s0 !== undefined) {
        //first the special cases
        if (!e0) {
            //data set contains only one data point
            //add a single horizontal section from s0.t to s0.t + targetInterval
            spline.push(codec.spline(undefined, s0, undefined, undefined, targetInterval));
        }
        else if (!e1) {
            //the whole data set only consisted of two data points
            if (e0.t - s0.t < gapDetectionThreshold) {
                //add a straight section from s0 to e0
                codec
                    .spline(undefined, s0, e0, undefined, targetInterval)
                    .forEach((part) => spline.push([part]));
            }
            else {
                //add two horizontal sections (s0.t to s0.t + targetInterval) and (e0.t - targetInterval to e0.t)
                spline.push(codec.spline(undefined, s0, undefined, undefined, targetInterval));
                spline.push(codec.spline(undefined, undefined, e0, undefined, targetInterval));
            }
        }
        else {
            //normal case
            spline.push(section);
            while (s0 !== undefined && e0 !== undefined) {
                if (e0.t - s0.t < gapDetectionThreshold) {
                    //s0 and e0 are within accepted range so form a normal interval
                    const splineParts = codec.spline(s1, s0, e0, e1, targetInterval);
                    //append first part to section array
                    //append the rest in a separate spline section and point section to last added part
                    splineParts.forEach((part, i) => {
                        if (i > 0) {
                            //create new section when not first part
                            section = [];
                            spline.push(section);
                        }
                        //add the part
                        section.push(part);
                    });
                }
                else {
                    //s0 and e0 too far apart so a gap detected! -> start new section
                    if (section.length > 0) {
                        //s0 is the end of current section
                        //create a new section
                        section = [];
                        spline.push(section);
                    }
                    else {
                        //last section is still empty so s0 is a solo point
                        spline.splice(spline.length - 1, 0, codec.spline(undefined, undefined, s0, undefined, targetInterval));
                        //(empty) section now after single point s0
                    }
                }
                //advance
                s1 = s0;
                s0 = e0;
                e0 = e1;
                e1 = undefined;
                if (e0 !== undefined) {
                    const i = findFirst(e0.i + 1, data);
                    if (i !== undefined) {
                        e1 = { t: time(i), v: data(i), i: i };
                    }
                }
            }
            if (section.length === 0) {
                //last section is still empty so s0 is a solo point
                section.push(codec.spline(undefined, undefined, s0, undefined, targetInterval)[0]);
            }
        }
    }
    else {
        //empty dataset
    }
    return spline;
};
